import { addToast } from '@features/components/Notification/notificationSlice.ts';
import i18n from '@providers/i18n';
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { SESSION_EXPIRED_REDIRECT_TOAST_KEY } from '@shared/constants/auth.ts';
import localStore from '@shared/utils/localStore.ts';
import { Mutex } from 'async-mutex';

import { logout, updateTokens } from './reducers/authSlice.ts';
import { HotelUserTokenDto } from './services/hotelAuthApi.ts';

interface ErrorResponse {
  code: string;
  message: string;
  module: string;
}

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_PATH,
  prepareHeaders: headers => {
    // this method should retrieve the token without a hook
    const { accessToken } = localStore.getTokens();

    if (accessToken && !headers.has('authorization')) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const toHotelUserTokenDto = (data: unknown): HotelUserTokenDto | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Partial<HotelUserTokenDto> & {
    accessToken?: string;
    refreshToken?: string;
  };

  if (payload.tokens?.accessToken && payload.tokens?.refreshToken) {
    return payload as HotelUserTokenDto;
  }

  if (payload.accessToken && payload.refreshToken) {
    return {
      tokens: {
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
      },
    };
  }

  return null;
};

const handleSessionExpired = (api: BaseQueryApi) => {
  sessionStorage.setItem(SESSION_EXPIRED_REDIRECT_TOAST_KEY, '1');
  api.dispatch(logout());
  window.location.replace('/login');
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();
  // Prefer in-memory tokens, but fall back per-field to local storage to avoid race conditions.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state = api.getState() as any;
  const stateTokens = state?.auth?.tokens;
  const storedTokens = localStore.getTokens();
  const refreshToken = stateTokens?.refreshToken ?? storedTokens.refreshToken;
  const requestUrl = typeof args === 'string' ? args : args.url;
  let result = await baseQuery(args, api, extraOptions);

  const status = result.error?.status;

  if (status === 403) {
    const authReady = Boolean(state?.auth?.user) && state?.auth?.permissionsReady === true;
    if (authReady) {
      window.location.replace('/permissionDenied');
    }
    return result;
  }

  if (result.error && Number(result.error.status) === 400) {
    const errorResponse = result.error.data as ErrorResponse;
    const message = i18n.t(`${errorResponse.module}.errors.${errorResponse.code}`, {
      defaultValue: errorResponse.message,
    });
    api.dispatch(addToast({ message, type: 'error' }));

    return result;
  }

  if (result.error && Number(result.error.status) > 401) {
    const message =
      'data' in result.error
        ? `${(result.error.data as { message: string }).message}, please try again later.`
        : 'Unable to reach the server, please try again later.';
    api.dispatch(addToast({ message, type: 'error' }));

    return result;
  }

  const isRefreshRequest = requestUrl.includes('/hotel-auth/refresh');

  if (refreshToken && result.error && Number(result.error.status) === 401 && !isRefreshRequest) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await baseQuery(
          {
            url: `/hotel-auth/refresh`,
            headers: {
              authorization: `Bearer ${refreshToken}`,
              'refresh-token': refreshToken,
            },
            method: 'POST',
            body: {
              refreshToken,
            },
          },
          api,
          extraOptions,
        );

        const normalizedRefreshTokens = toHotelUserTokenDto(refreshResult.data);
        if (normalizedRefreshTokens) {
          api.dispatch(updateTokens(normalizedRefreshTokens));
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
          if (result.error && Number(result.error.status) === 401) {
            handleSessionExpired(api);
            return result;
          }
        } else {
          handleSessionExpired(api);
          return result;
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
      if (result.error && Number(result.error.status) === 401) {
        handleSessionExpired(api);
        return result;
      }
    }
  } else if (result.error && Number(result.error.status) === 401) {
    // No valid tokens to refresh; ensure logout to trigger redirect via navigation guard
    handleSessionExpired(api);
    return result;
  }

  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const entrySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Lost Items'],
  endpoints: () => ({}),
});
